/**
 *
 * CompanyForm
 *
 */

import CustomModal from '../CustomModal';
import InputMask from '../InputMask';
import InputText from '../InputText';
import Select, { IOption } from '../Select';
import UploadLogo from '../UploadLogo';
import { FormEvent, useState } from 'react';
import { Box, Button, Flex, Heading, HStack, SystemStyleObject, Text, useDisclosure } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ROUTES } from 'config/routes';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { validateCep, validateCNPJ } from 'validations-br';
import * as yup from 'yup';
import { IRequestCompany, ISearchCEP, searchCep } from 'services/http/company';
import { BRAZILIAN_STATES, CEP_MASK, CNPJ_MASK, PHONE_MASK } from 'utils/constants';
import { handleIntegerKeyDown } from 'utils/form';
import { unmask } from 'utils/formatCep';
import { ResponseErrors } from 'utils/parseErrors';
import { formatPhone, unMaskPhone } from 'utils/phone';

export interface ICompanyForm {
	cnpj: string;
	company: string;
	state: IOption;
	city: string;
	cep: string;
	neighborhood: string;
	street: string;
	number: string;
	complement?: string;
	logo?: string;
	responsibleName: string;
	responsiblePhone: string;
	responsibleEmail: string;
	login: string;
	syonet: string;
	nisaBiCompanyName: string;
}

interface CompanyFormProps {
	defaultValues?: ICompanyForm;
	isEditing?: boolean;
	onSubmitForm?: (values: IRequestCompany) => void;
	isLoading?: boolean;
	isReadOnly?: boolean;
}

const CompanyForm = ({
	defaultValues,
	onSubmitForm,
	isEditing = false,
	isReadOnly = false,
	isLoading,
}: CompanyFormProps) => {
	const styles: Record<string, SystemStyleObject> = {
		container: {
			width: '100%',
			boxShadow: 'rgba(99, 99, 99, 0.2) 0px 0.125rem 0.5rem 0px',
			backgroundColor: 'white',
			borderRadius: '0.5rem',
			maxWidth: { base: '100%', md: '48.688rem' },
			padding: '3rem',
			flexDirection: 'column',
		},
		header: {
			w: '100%',
			alignItems: 'baseline',
			justifyContent: 'space-between',
			gap: '1.5rem',
			flexDirection: {
				base: 'column',
				md: 'row',
			},
		},
		heading: {
			fontSize: '1.87rem',
			lineHeight: '3.25rem',
			color: '#000',
			fontWeight: 'semibold',
		},
		spinner: {
			boxSize: 4,
			color: 'white',
		},
		spinnerBox: {
			justifyContent: 'center',
		},
		form: {
			display: 'flex',
			flexDirection: 'column',
			gap: '0.625rem',
		},
		uppercaseText: {
			textTransform: 'uppercase',
		},
		lowercaseText: {
			textTransform: 'lowercase',
		},
		selectContainer: {
			flexDirection: { base: 'column', md: 'row' },
			gap: '1rem',
		},
		biggerBox: {
			width: { base: '100%', md: '28.188rem' },
		},
		smallerBox: {
			width: { base: '100%', md: '13.563rem' },
		},
		title: {
			color: '#2E769B',
			fontSize: '1.25rem',
			fontWeight: '700',
		},
		actions: {
			mt: '2rem',
			textAlign: 'right',
			fontSize: '1rem',
		},
		actionsButtons: {
			display: 'flex',
			justifyContent: 'end',
			gap: {
				base: '1rem',
				sm: '1.875rem',
			},
			flexDirection: {
				base: 'column',
				lg: 'row',
			},
		},
		button: {
			w: { base: '100%', lg: '9.25rem' },
			fontSize: { base: '1rem', lg: '1.375rem' },
			h: { base: '4rem' },
		},
	};

	const schema = yup.object().shape({
		cnpj: yup
			.string()
			.required('Este campo é obrigatório.')
			.test({
				message: 'O número do CNPJ é inválido.',
				test: value => (value ? validateCNPJ(value || '') : true),
			}),
		company: yup
			.string()
			.required('Este campo é obrigatório.')
			.max(100, 'Máximo de caracteres 100.')
			.min(10, 'Mínimo de caracteres 10.'),
		cep: yup
			.string()
			.required('Este campo é obrigatório.')
			.test({
				message: 'O número do CEP é inválido.',
				test: value => (value ? validateCep(value || '') : true),
			}),
		neighborhood: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		state: yup
			.object()
			.required('Este campo é obrigatório.')
			.default(undefined)
			.nullable()
			.shape({
				label: yup.string().required('Este campo é obrigatório.'),
				value: yup.string().required('Este campo é obrigatório.'),
			}),
		city: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		street: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		number: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		complement: yup.string().max(100, 'Máximo de caracteres 100.'),
		responsibleName: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		responsiblePhone: yup
			.string()
			.required('Este campo é obrigatório.')
			?.test({
				message: 'O número de telefone é inválido.',
				test: value => formatPhone(value || '')?.length === 11,
			})
			.nullable(),
		responsibleEmail: yup.string().email('Formato de e-mail inválido.').required('Este campo é obrigatório.'),
		login: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		syonet: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		nisaBiCompanyName: yup.string().required('Este campo é obrigatório.').max(100, 'Máximo de caracteres 100.'),
		logo: yup.mixed(),
	});

	const navigate = useNavigate();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isOpenCancel, onOpen: onOpenCancel, onClose: onCloseCancel } = useDisclosure();
	const [formData, setFormData] = useState<ICompanyForm>();

	const handleGoBack = () => {
		!isEditing ? onCloseCancel() : onClose();
		navigate(ROUTES.listCompanies);
	};

	const {
		register,
		handleSubmit,
		control,
		setValue,
		watch,
		getValues,
		reset,
		setError,
		clearErrors,
		formState: { errors, isDirty },
	} = useForm<ICompanyForm>({
		resolver: yupResolver(schema),
		defaultValues: { ...defaultValues },
	});

	const formValues = watch();
	const pic = watch('logo');

	const handleOpenModal = () => {
		const currentLogo = watch('logo');
		setFormData({ ...formValues, logo: currentLogo || formValues?.logo });
		onOpen();
	};

	const { mutate: searchCEPMutation } = useMutation<ISearchCEP, AxiosError<ResponseErrors>, string>({
		mutationFn: cep => searchCep(cep),
		onSuccess: data => {
			const values = {
				...getValues(),
				street: data?.logradouro || '',
				neighborhood: data?.bairro || '',
				city: data?.localidade || '',
				state: data?.erro ? undefined : { label: data?.uf, value: data?.uf },
			};

			reset(values, { keepDirty: true });

			if (data.erro) {
				setError('cep', {
					type: 'manual',
					message: 'CEP inválido, tente novamente',
				});
			} else {
				clearErrors(['cep', 'city', 'neighborhood', 'number', 'state', 'complement', 'street']);
			}
		},
	});

	const handleSearchCEP = (formEvent: FormEvent<HTMLDivElement>) => {
		const input = formEvent as React.ChangeEvent<HTMLInputElement>;

		const value = input.target.value.replace(/\D/g, '');

		if (value.length === 8) {
			searchCEPMutation(value);
		}
	};

	const onSubmit = (values: ICompanyForm) => {
		const hasFile = values.logo?.length || pic?.length || formValues?.logo?.length;
		const file = values?.logo || pic || formValues?.logo;

		const dataForm: IRequestCompany = {
			cnpj: unmask(values?.cnpj),
			company: values?.company,
			postalCode: unmask(values?.cep),
			neighborhood: values?.neighborhood,
			state: String(values?.state?.value),
			city: String(values?.city),
			street: values?.street,
			number: values?.number,
			complement: values?.complement,
			storeLogo: hasFile ? file : undefined,
			managerName: values?.responsibleName,
			managerPhone: unMaskPhone(values?.responsiblePhone),
			managerEmail: values?.responsibleEmail,
			autoAvaliarLogin: values?.login,
			syonetLogin: values?.syonet,
			autoAvaliarCompanyName: values?.login,
			nisaBiCompanyName: values?.nisaBiCompanyName,
			nisaSyonetCompanyId: values?.syonet,
		};

		onSubmitForm?.(dataForm);
	};

	return (
		<>
			<Flex sx={styles?.container}>
				<HStack sx={styles?.header}>
					<Heading sx={styles.heading}>{isEditing ? 'Edição de Unidade' : 'Cadastro de Unidade'}</Heading>
				</HStack>
				<Box
					sx={styles?.form}
					as="form"
					noValidate
					onSubmit={isEditing ? handleSubmit(handleOpenModal) : handleSubmit(onSubmit)}
				>
					<Controller
						name="cnpj"
						control={control}
						render={({ field }) => (
							<InputMask
								disabled={isReadOnly}
								isReadOnly={isReadOnly}
								value={field?.value || ''}
								onChange={e => {
									field?.onChange(e);
								}}
								mask={CNPJ_MASK}
								name="cnpj"
								label="CNPJ"
								placeholder="Ex.: 99.999.999/9999-99"
								errorMessage={errors?.cnpj?.message}
								data-testid="input--cnpj"
								required
							/>
						)}
					/>
					<InputText
						label="Nome da Unidade"
						disabled={isReadOnly}
						isReadOnly={isReadOnly}
						placeholder="Nome da Unidade"
						name="company"
						register={register}
						errorMessage={errors?.company?.message}
						data-testid="input--company"
						type="text"
						required
						maxLength={100}
					/>
					<Flex sx={styles?.selectContainer}>
						<Box sx={styles?.smallerBox}>
							<Controller
								name="cep"
								control={control}
								render={({ field }) => (
									<InputMask
										disabled={isReadOnly}
										isReadOnly={isReadOnly}
										value={field?.value || ''}
										onChange={e => {
											field?.onChange(e);
											handleSearchCEP(e);
										}}
										mask={CEP_MASK}
										name="cep"
										label="CEP"
										placeholder="Ex.: 99999-999"
										errorMessage={errors?.cep?.message}
										data-testid="input--cep"
										required
									/>
								)}
							/>
						</Box>
						<Box sx={styles?.biggerBox}>
							<InputText
								label="Bairro"
								disabled={isReadOnly || !formValues?.cep}
								isReadOnly={isReadOnly}
								placeholder="Bairro"
								name="neighborhood"
								register={register}
								errorMessage={errors?.neighborhood?.message}
								data-testid="input--neighborhood"
								type="text"
								required
								maxLength={100}
							/>
						</Box>
					</Flex>
					<Flex sx={styles?.selectContainer}>
						<Box sx={styles?.smallerBox}>
							<Controller
								name="state"
								control={control}
								render={({ field }) => (
									<Select
										isDisabled={isReadOnly || !formValues?.cep}
										isReadOnly={isReadOnly}
										label="Estado"
										placeholder="Estado"
										onChange={field?.onChange}
										value={field?.value}
										options={BRAZILIAN_STATES}
										errorMessage={errors?.state?.message}
										dataTestId="input--state"
										required
									/>
								)}
							/>
						</Box>
						<Box sx={styles?.biggerBox}>
							<InputText
								label="Município"
								disabled={isReadOnly || !formValues?.cep}
								isReadOnly={isReadOnly}
								placeholder="Município"
								name="city"
								register={register}
								errorMessage={errors?.city?.message}
								data-testid="input--city"
								type="text"
								required
								maxLength={100}
							/>
						</Box>
					</Flex>
					<Flex sx={styles?.selectContainer}>
						<Box sx={styles?.biggerBox}>
							<InputText
								label="Rua"
								disabled={isReadOnly || !formValues?.cep}
								isReadOnly={isReadOnly}
								placeholder="Rua"
								name="street"
								register={register}
								errorMessage={errors?.street?.message}
								required
								data-testid="input--street"
								type="text"
								maxLength={100}
							/>
						</Box>
						<Box sx={styles?.smallerBox}>
							<InputText
								label="Número"
								disabled={isReadOnly || !formValues?.cep}
								isReadOnly={isReadOnly}
								placeholder="Número"
								name="number"
								register={register}
								errorMessage={errors?.number?.message}
								required
								data-testid="input--numero"
								type="text"
								maxLength={100}
							/>
						</Box>
					</Flex>
					<InputText
						label="Complemento"
						disabled={isReadOnly || !formValues?.cep}
						isReadOnly={isReadOnly}
						placeholder="Complemento"
						name="complement"
						register={register}
						errorMessage={errors?.complement?.message}
						data-testid="input--complement"
						type="text"
						maxLength={100}
					/>
					<UploadLogo
						label="Logo da loja"
						name="logo"
						data-testid="input--logo"
						setValue={setValue}
						value={formValues?.logo || pic}
						isReadOnly={isReadOnly}
						errorMessage={errors?.logo?.message}
					/>
					<Text sx={styles?.title}>Responsável</Text>
					<Flex sx={styles?.selectContainer}>
						<Box sx={styles?.biggerBox}>
							<InputText
								label="Nome do Responsável"
								disabled={isReadOnly}
								isReadOnly={isReadOnly}
								placeholder="Nome do Responsável"
								name="responsibleName"
								register={register}
								errorMessage={errors?.responsibleName?.message}
								required
								data-testid="input--responsavel"
								type="text"
								sx={styles.uppercaseText}
								maxLength={100}
							/>
						</Box>
						<Box sx={styles?.smallerBox}>
							<Controller
								name="responsiblePhone"
								control={control}
								render={({ field }) => (
									<InputMask
										disabled={isReadOnly}
										isReadOnly={isReadOnly}
										mask={PHONE_MASK}
										onChange={field?.onChange}
										value={field?.value || ''}
										label="Telefone"
										placeholder="Telefone"
										name="responsiblePhone"
										errorMessage={errors?.responsiblePhone?.message}
										required
										data-testid="input--responsiblePhone"
									/>
								)}
							/>
						</Box>
					</Flex>
					<InputText
						label="E-mail"
						disabled={isReadOnly}
						isReadOnly={isReadOnly}
						placeholder="E-mail"
						name="responsibleEmail"
						register={register}
						errorMessage={errors?.responsibleEmail?.message}
						required
						data-testid="input--responsibleEmail"
						type="email"
						sx={styles.lowercaseText}
						maxLength={100}
					/>
					<Text sx={styles?.title}>Login</Text>
					<InputText
						label="AutoAvaliar"
						disabled={isReadOnly}
						isReadOnly={isReadOnly}
						placeholder="AutoAvaliar Unidade"
						name="login"
						register={register}
						errorMessage={errors?.login?.message}
						required
						data-testid="input--login"
						type="text"
						maxLength={100}
					/>
					<InputText
						label="Syonet"
						disabled={isReadOnly}
						isReadOnly={isReadOnly}
						placeholder="Nisa Syonet Id"
						name="syonet"
						register={register}
						errorMessage={errors?.syonet?.message}
						required
						data-testid="input--syonet"
						maxLength={100}
						type="number"
						step="1"
						onKeyDown={handleIntegerKeyDown}
					/>

					<InputText
						label="Nisa BI"
						disabled={isReadOnly}
						isReadOnly={isReadOnly}
						placeholder="Nisa BI Company Name"
						name="nisaBiCompanyName"
						register={register}
						errorMessage={errors?.nisaBiCompanyName?.message}
						required
						data-testid="input--bi-company-name"
						type="text"
						sx={styles?.uppercaseText}
						maxLength={100}
					/>

					{!isReadOnly && (
						<Box sx={styles.actions}>
							<Box sx={styles.actionsButtons}>
								<Button
									sx={styles.button}
									data-testid="button--cancel"
									variant="outlined"
									onClick={isDirty ? () => onOpenCancel() : () => handleGoBack()}
								>
									Voltar
								</Button>
								<Button type="submit" sx={styles.button} isLoading={isLoading} data-testid="button--submit">
									{isEditing ? 'Salvar' : 'Cadastrar'}
								</Button>
							</Box>
						</Box>
					)}
				</Box>

				<CustomModal
					title=""
					body="Os dados editados não foram salvos e as alterações serão perdidas. Deseja continuar mesmo assim?"
					isOpen={isOpenCancel}
					onClose={onCloseCancel}
					actions={[
						{
							label: 'Não',
							type: 'outlined',
							onClick: onCloseCancel,
							datatestid: 'button--cancel',
						},
						{
							label: 'Sim',
							type: 'solid',
							onClick: handleGoBack,
							datatestid: 'button--confirm',
						},
					]}
				/>

				<CustomModal
					title="Confirmar Alterações na Unidade"
					body={
						<p
							style={{
								fontWeight: '400',
								fontFamily: 'Boston',
								fontSize: '1.563rem',
							}}
						>
							Você deseja salvar as alterações feitas no cadastro desta unidade?
						</p>
					}
					isOpen={isOpen}
					onClose={onClose}
					actions={[
						{
							label: 'Voltar',
							type: 'outlined',
							onClick: onClose,
							datatestid: 'button--cancel',
						},
						{
							label: 'Salvar Alterações',
							type: 'solid',
							onClick: () => {
								if (formData) {
									onSubmit(formData!);
									onClose();
								}
							},
							datatestid: 'button--confirm',
						},
					]}
				/>
			</Flex>
		</>
	);
};

export default CompanyForm;
